import { format, formatDistanceStrict } from 'date-fns';
import { CheckCircle, XCircle, Clock, ExternalLink } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { formatDuration, formatFileSize } from '@/client/lib/format';
import { Button } from '@/client/components/ui/button';
import {
  DetailCard,
  DetailList,
  DetailRow,
} from '@/client/components/ui/detail-view';
import type { CaptionPipelineData } from '@shared/types/events';

function StepStatus({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1 text-sm ${ok ? 'text-green-500' : 'text-destructive'}`}>
      {ok ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      {label}
    </span>
  );
}

function PendingStatus({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
      <Clock className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}

interface CaptionPipelineCardProps {
  pipeline: CaptionPipelineData;
  /** When true, hides the Live Session row (event is currently live, previous session data is stale) */
  isLive?: boolean;
}

export function CaptionPipelineCard({ pipeline, isLive = false }: CaptionPipelineCardProps) {
  const sessionEnded = ['ended', 'end', 'End of Transcribe'].includes(pipeline.leacStatus ?? '');
  const isPublished = pipeline.publishedAt !== null;
  const hasVtt = pipeline.captionVtt.exists;

  // Compute time since publish for missing captions
  let timeSincePublish: string | null = null;
  if (isPublished && !hasVtt && pipeline.publishedAt) {
    timeSincePublish = formatDistanceStrict(new Date(pipeline.publishedAt), new Date(), { addSuffix: true });
  }

  return (
    <DetailCard title="Caption Pipeline">
      <DetailList>
        {!isLive && (
          <DetailRow
            label="Live Session"
            value={
              <div className="flex items-center gap-2">
                {pipeline.sessionStart && (
                  <span className="font-mono text-sm">
                    {format(new Date(pipeline.sessionStart), 'MMM d, HH:mm')}
                  </span>
                )}
                {sessionEnded
                  ? <StepStatus ok label={pipeline.leacStatus ?? 'ended'} />
                  : <PendingStatus label={pipeline.leacStatus ?? 'unknown'} />
                }
              </div>
            }
          />
        )}
        {pipeline.captionDuration !== null && (
          <DetailRow
            label="Session Duration"
            value={<span className="font-mono text-sm">{formatDuration(pipeline.captionDuration)}</span>}
          />
        )}
        <DetailRow
          label="Published"
          value={
            isPublished && pipeline.publishedAt ? (
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">
                  {format(new Date(pipeline.publishedAt), 'MMM d, HH:mm')}
                </span>
                <StepStatus ok label="archive" />
              </div>
            ) : (
              <PendingStatus label="not published" />
            )
          }
        />
        <DetailRow
          label="Caption VTT"
          value={
            hasVtt ? (
              <div className="flex items-center gap-2">
                {pipeline.captionVtt.createdAt && (
                  <span className="font-mono text-sm">
                    {format(new Date(pipeline.captionVtt.createdAt), 'MMM d, HH:mm')}
                  </span>
                )}
                <StepStatus ok label={formatFileSize(pipeline.captionVtt.fileSize)} />
              </div>
            ) : isPublished ? (
              <div className="flex items-center gap-2">
                <StepStatus ok={false} label="missing" />
                {timeSincePublish && (
                  <span className="text-xs text-muted-foreground">
                    (published {timeSincePublish})
                  </span>
                )}
              </div>
            ) : (
              <PendingStatus label="awaiting publish" />
            )
          }
        />
        {pipeline.timeToCaptionSeconds !== null && (
          <DetailRow
            label="Time to Caption"
            value={<span className="font-mono text-sm">{formatDuration(pipeline.timeToCaptionSeconds)}</span>}
          />
        )}
      </DetailList>

      {/* VTT Analyzer button */}
      {hasVtt && pipeline.captionVtt.path && (
        <div className="mt-3 pt-3 border-t">
          <Button asChild variant="outline" size="sm">
            <Link
              to="/utilities/vtt-analyzer"
              search={{ url: pipeline.captionVtt.path }}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              Analyze VTT
            </Link>
          </Button>
        </div>
      )}
    </DetailCard>
  );
}
